// useGooseBerryForm: consolidates ALL persistent GooseBerry form state into a
// single hook so the parent view stops carrying 13+ useState declarations and
// 13+ useEffect persistence calls. This also drops the dependency count of the
// `onRun` callback from 16 → 2 (form snapshot + image state), which fixes the
// react-hooks/exhaustive-deps complexity warning.
import { useCallback, useEffect, useMemo, useReducer } from "react";
import { lsGet, lsSet, lsClearPrefix } from "@/lib/storage";
import {
  DATABASE_TYPES,
  PREDEFINED_METHODS,
  FIDELITY_LEVELS,
} from "@/data/gooseBerryData";

export const LS_PREFIX = "synberry-lab.synberry.";

// All persisted field keys + their default values in one table, adding a new
// persisted field is now a one-line change instead of three (state + load + save).
const PERSISTED_FIELDS = Object.freeze({
  projectName:       { storage: LS_PREFIX + "projectName",       default: ""           },
  targetGroup:       { storage: LS_PREFIX + "targetGroup",       default: ""           },
  societalTheme:     { storage: LS_PREFIX + "societalTheme",     default: ""           },
  designPhase:       { storage: LS_PREFIX + "designPhase",       default: "Prototype"  },
  assumptions:       { storage: LS_PREFIX + "assumptions",       default: ""           },
  fidelity:          { storage: LS_PREFIX + "fidelity",          default: "mid"        },
  customText:        { storage: LS_PREFIX + "customText",        default: ""           },
  customType:        { storage: LS_PREFIX + "customType",        default: DATABASE_TYPES[0] },
  customFileName:    { storage: LS_PREFIX + "customFileName",    default: ""           },
  customFileContent: { storage: LS_PREFIX + "customFileContent", default: ""           },
  selectedMethodId:  { storage: LS_PREFIX + "selectedMethodId",  default: PREDEFINED_METHODS[0].id },
  customMethods:     { storage: LS_PREFIX + "customMethods",     default: []           },
  mode:              { storage: LS_PREFIX + "mode",              default: "feedback"   },
});

function loadInitialState() {
  const out = {};
  for (const [key, cfg] of Object.entries(PERSISTED_FIELDS)) {
    out[key] = lsGet(cfg.storage, cfg.default);
  }
  return out;
}

function defaultState() {
  const out = {};
  for (const [key, cfg] of Object.entries(PERSISTED_FIELDS)) {
    out[key] = cfg.default;
  }
  return out;
}

function reducer(state, action) {
  if (action.type === "SET") {
    return { ...state, [action.key]: action.value };
  }
  if (action.type === "RESET") {
    return defaultState();
  }
  return state;
}

/**
 * Bundles all persistent GooseBerry form state into a single object so
 * downstream `useCallback`/`useEffect` only depend on ONE reference.
 *
 * @returns {{
 *   form: Record<string, any>,
 *   setField: (key: string, value: any) => void,
 *   resetAll: () => void,
 *   derived: { methodsAll, selectedMethod, fidelityLabel },
 * }}
 */
export function useGooseBerryForm() {
  const [form, dispatch] = useReducer(reducer, undefined, loadInitialState);

  // Persist whenever ANY field changes. We persist all 13 fields on every
  // update, they're tiny strings so the cost is negligible, and this keeps
  // the persistence logic to a single useEffect (down from 13).
  useEffect(() => {
    for (const [key, cfg] of Object.entries(PERSISTED_FIELDS)) {
      lsSet(cfg.storage, form[key]);
    }
  }, [form]);

  const setField = useCallback((key, value) => {
    dispatch({ type: "SET", key, value });
  }, []);

  const resetAll = useCallback(() => {
    lsClearPrefix(LS_PREFIX);
    dispatch({ type: "RESET" });
  }, []);

  // Derived selectors so the parent view does not recompute them inline.
  const methodsAll = useMemo(
    () => [...PREDEFINED_METHODS, ...form.customMethods],
    [form.customMethods]
  );
  const selectedMethod = useMemo(
    () => methodsAll.find((m) => m.id === form.selectedMethodId) || methodsAll[0],
    [methodsAll, form.selectedMethodId]
  );
  const fidelityLabel = useMemo(
    () => (FIDELITY_LEVELS.find((f) => f.id === form.fidelity) || FIDELITY_LEVELS[1]).label,
    [form.fidelity]
  );

  return {
    form,
    setField,
    resetAll,
    derived: { methodsAll, selectedMethod, fidelityLabel },
  };
}
